
import { WizDateUtils } from '@wizeoo/akm-core-lib';
import { ViewData } from '../../model/view.data';
import { Model } from '../../model/model';
import { FeiVO } from '../../vo/workingdata/fei.vo';
import { BaselineEngine, ComputeResult, FIELD_KIND, FeiKeyDef, FormulaCoreEngine, MeasMode, WidgetLine, WizConstants } from '@wizeoo/akm-compute-lib';
import { WizColDef } from '../workingdata/fei.engine';
import { WidgetConfigurationItemObj } from '@wizeoo/akm-message-lib';
import { PivotFunction } from '../baselines/pivot-function.obj';
import { IWidget } from '../widgets/i-widget';

/**
 * contient les fonctions disponibles pour l'aggrégation de données dans le pivot
 */
export class AggregateEngine {
  
  protected _model: Model = null;
  constructor(model: Model) {
    this._model = model;
  }
  
  
  /** Map des fonctions map[code de la fonction] = [description, nb paramètres] */
  private _functionMap: Map<string, PivotFunction> = null;

  /** constantes de fonctions d'aggrégats */
  public static AGG_SUM: string = 'SUM';
  public static  AGG_COUNT: string = 'COUNT';
  public static  AGG_COUNTDISTINCT: string = 'COUNTDISTINCT';
  public static  AGG_TWIN: string = 'TWIN';
  public static  AGG_AVG: string = 'AVG';
  public static  AGG_MAX: string = 'MAX';
  public static  AGG_MIN: string = 'MIN';
  public static  AGG_EMPTY: string = 'EMPTY';
  public static  AGG_NOTEMPTY: string = 'NOTEMPTY';
  public static  AGG_BOOLEAN: string = 'BOOLEAN';
  public static  AGG_LIST: string = 'LIST';
  public static  AGG_COUNT_TRUE: string = 'COUNTTRUE';
  public static  AGG_COUNT_FALSE: string = 'COUNTFALSE';
  public static  AGG_COMPUTE: string = 'COMPUTE';
  public static  AGG_VOID: string = 'VOID';

  /**
   * Initialisation de la map des fonctions
   *
   * @return Map<String, String> Map des fonctions alimentée
   */
  public functionMap(): Map<string, PivotFunction> {
    if (this._functionMap === null) {
      this._functionMap = new Map<string, PivotFunction>();
      this._functionMap.set('COMPUTE', new PivotFunction('COMPUTE', 'compute on aggregate', null, true, true, true, true, true));
      this._functionMap.set('SUM', new PivotFunction('SUM', 'sum elements', null, true, false, false, false));
      this._functionMap.set('COUNT', new PivotFunction('COUNT', 'count elements', null, true, true, true, true, false, FIELD_KIND.INT));
      this._functionMap.set('COUNTDISTINCT', new PivotFunction('COUNTDISTINCT', 'count distincts items', null, true, true, true, true, false, FIELD_KIND.INT));
      this._functionMap.set('TWIN', new PivotFunction('TWIN', 'true if twins', null, true, true, true, true, false, FIELD_KIND.BOOLEAN));
      this._functionMap.set('AVG', new PivotFunction('AVG', 'average val of elements', null, true, true, false, false, true));
      this._functionMap.set('MAX', new PivotFunction('MAX', 'max value of collection', null, true, true, false, false, true));
      this._functionMap.set('MIN', new PivotFunction('MIN', 'min value of collection', null, true, true, false, false, true));
      this._functionMap.set('EMPTY', new PivotFunction('EMPTY', 'count empty values', null, true, true, true, true, false, FIELD_KIND.INT));
      this._functionMap.set('NOTEMPTY', new PivotFunction('NOTEMPTY', 'count non empty values', null, true, true, true, true, false, FIELD_KIND.INT));
      this._functionMap.set('BOOLEAN', new PivotFunction('BOOLEAN', 'true, false or void', null, false, false, false, true, false, FIELD_KIND.BOOLEAN));
      this._functionMap.set('LIST', new PivotFunction('LIST', 'list of values', null, false, false, true, false, true, FIELD_KIND.STRING));
      this._functionMap.set('COUNTTRUE', new PivotFunction('COUNTTRUE', 'count true', null, false, false, false, true, false, FIELD_KIND.INT));
      this._functionMap.set('COUNTFALSE', new PivotFunction('COUNTFALSE', 'count false', null, false, false, false, true, false, FIELD_KIND.INT));
      this._functionMap.set('VOID', new PivotFunction('VOID', 'do nothing', null, true, true, true, true, true));
    }
    return this._functionMap;
  }

  /**
   * mappeur de fonction par code
   *
   * @param functionName 
   * @param fei fei cible pour calcul
   * @param lines WidgetLine[]
   * @param pkd pour résolution du code#mode ou du code chrono
   * @param field champ sur chronoCode (pas forcément startDate)
   * @returns le résultat aggrégé
   */
  public call(functionName: string, fei: FeiVO, lines: WidgetLine[], fkd: FeiKeyDef, field?: string): string {
    const func: string = functionName || AggregateEngine.AGG_SUM;
    return this[func](fei, lines, fkd, field);
  }

  /**
   * selon la nature du pkd, va retourner la valeur OU la rawKey (chronoCodes)
   * 
   * @param fei 
   * @param line WidgetLine cible
   * @param fkd pour prise de la mesure / code chrono
   * @param field champ éventuel sur le chrono
   * @returns 
   */
  private getRawKeyOrValue(fei: FeiVO, line: WidgetLine, fkd: FeiKeyDef, field?: string): string {
    if(fkd.isMeasure) {
      // patch pour gestion des mesures LIST (le rendu de la pop)
      if(fei.scenario === WizConstants.SCEN_FAKE_KEY_CODE && !line.cells?.has(fkd.fullCode)) {
        line.cells.set(fkd.fullCode, { k: line.key, m: fkd.fullCode, l: line.level, sc: line.bl.scen });
      }

      return BaselineEngine.getValue(fei, line, line?.cells?.get(fkd.fullCode));
    } else {
      return BaselineEngine.getLabel(fei, line, fkd.fullCode, field);
    }
  }

  /**
   * formate le nombre avec les bonnes décimales
   *
   * @param val la valeur
   * @param pkd le pkd contenant la précision de calcul
   * @returns un number au format voule
   */
  public toCleanNumberStr(val: number, fkd: FeiKeyDef): string {
    if (val && !isNaN(val)) {
      return (Math.round(val * Math.pow(10, fkd.nbDecimal)) / Math.pow(10, fkd.nbDecimal)).toString();
    }
    return null;
  }

  /**
   * ne fait rien
   */
  public VOID(fei: FeiVO, lines: WidgetLine[], fkd: FeiKeyDef, field?: string): string {
    return '';
  }

  /**
   * compte le nombre d'éléments (même les valeurs vides)
   */
  public COUNT(fei: FeiVO, lines: WidgetLine[], fkd: FeiKeyDef, field?: string): string {
    const tmpRes: number = lines.length;
    return tmpRes.toString();
  }

  /**
   * compte le nombre d'éléments distincts
   */
   public COUNTDISTINCT(fei: FeiVO, lines: WidgetLine[], fkd: FeiKeyDef, field?: string): string {
    let resultSet: Set<string> = new Set();
    lines.forEach((line: WidgetLine) => {
      // on prends la valeur de la baseline
      const val: string = this.getRawKeyOrValue(fei, line, fkd, field);
      if (val && val.toString().length > 0 ) {
        resultSet.add(val);
      }
    });
    // console.log('aggEng', pls, field, tmpRes);
    return resultSet.size.toString();
  }

  /**
   * identifie le nombre de doublons
   */
   public TWIN(fei: FeiVO, lines: WidgetLine[], fkd: FeiKeyDef, field?: string): string {
    let resultSet: Set<string> = new Set();
    let twins: number = 0;
    lines.forEach((line: WidgetLine) => {
      // on prends la valeur de la baseline
      const val: string = this.getRawKeyOrValue(fei, line, fkd, field);
      if (val && val.toString().length > 0 && Number(val) != 0 ) {
        if(resultSet.has(val)) {
          twins++;
        } else {
          resultSet.add(val);
        }
      }
    });
    // console.log('aggEng', pls, field, tmpRes);
    if (twins > 0) {
      return "1";
    }
    return "0";
  }


  /**
   * compte le nombre d'éléments vides
   */
  public EMPTY(fei: FeiVO, lines: WidgetLine[], fkd: FeiKeyDef, field?: string): string {
    let tmpRes: number = 0;
    lines.forEach((line: WidgetLine) => {
      // on prends la valeur de la baseline
      const val: string = this.getRawKeyOrValue(fei, line, fkd, field);
      if (!val || val === '') {
        tmpRes++;
      }
    });
    return tmpRes.toString();
  }

  /**
   * compte le nombre d'éléments non vides
   */
  public NOTEMPTY(fei: FeiVO, lines: WidgetLine[], fkd: FeiKeyDef, field?: string): string {
    let tmpRes: number = 0;
    lines.forEach((line: WidgetLine) => {
      // on prends la valeur de la baseline
      const val: string = this.getRawKeyOrValue(fei, line, fkd, field);
      if (val && val.toString().length > 0) {
        tmpRes++;
      }
    });
    return tmpRes.toString();
  }

  /**
   * fait la somme des valeurs
   */
  public SUM(fei: FeiVO, lines: WidgetLine[], fkd: FeiKeyDef, field?: string): string {
    let tmpRes: number = 0;
    lines.forEach((line: WidgetLine) => {
      // on prends la valeur de la baseline
      const val: string = this.getRawKeyOrValue(fei, line, fkd, field);
      if (val && val.toString().length > 0 && !isNaN(Number(val))) {
        tmpRes += Number(val);
      }
    });
    // console.log('aggEng', pls, field, tmpRes);
    return tmpRes.toString();
  }

  /**
   * fait la moyenne
   */
  public AVG(fei: FeiVO, lines: WidgetLine[], fkd: FeiKeyDef, field?: string): string {
    let tmpRes: number = 0;
    let nbres: number = 0;
    if (fkd.datakind === FIELD_KIND.DATE) {
      const dateNumArr: number[] = [];
      let refDate: number = null;
      let refDateDate: Date = null;
      lines.forEach((line: WidgetLine) => {
        // on mémorise les valeurs numérique dans le tableau
        const val: string = this.getRawKeyOrValue(fei, line, fkd, field);
        if (val && val.toString().length > 0 && !isNaN(Number(val))) {
          dateNumArr.push(Number(val));
          if (!refDate || Number(val) < refDate) {
            // on prend la date min
            refDate = Number(val);
          }
        }
      });

      if (dateNumArr.length === 0) {
        // rien
        return null;
      } else {
        refDateDate = WizDateUtils.numberToDate(refDate);
        nbres++; // on commence à 1, il y a la date de référence
        // console.log('arr length', dateNumArr.length);
        dateNumArr.splice(dateNumArr.indexOf(refDate), 1); // on enlève une instance de la date de référence (il peut y en avoir plusieurs)
        // console.log('arr length', dateNumArr.length);
        dateNumArr.forEach((numdate: number) => {
          const days = Math.floor(
            (WizDateUtils.numberToDate(numdate).getTime() - refDateDate.getTime()) / 1000 / 60 / 60 / 24
          );
          tmpRes += days;
          nbres++;
        });

        tmpRes = Math.floor(tmpRes / nbres); // on fait la moyenne du nombre de jours
        refDate = WizDateUtils.add(refDate, { days: tmpRes }) as number; // on ajoute les jours
        // console.log('refDate', refDate, tmpRes);
        return refDate.toString();
      }
    } else if (fkd.datakind === FIELD_KIND.TIME) {
      // on passe tout en secondes
      // puis on divise par le nombre d'heures dispo
      let inSecHour: number;
      lines.forEach((line: WidgetLine) => {
        // on mémorise les valeurs numérique dans le tableau
        const val: string = this.getRawKeyOrValue(fei, line, fkd, field);
        if (val && val.toString().length > 0 && !isNaN(Number(val))) {
          inSecHour = WizDateUtils.timeToSeconds(val);
          tmpRes += inSecHour;
          // console.log('inSecHour', val, inSecHour);
          nbres++;
        }
      });

      if (nbres === 0) {
        // rien
        return null;
      } else {
        let totRes = Math.floor(tmpRes / nbres); // on fait la moyenne du nombre de jours
        let secondsToTime: string = WizDateUtils.secondsToTime(totRes);
        // console.log('secondsToTime', secondsToTime, totRes, tmpRes, nbres);
        return secondsToTime;
      }
    }
    else {
      lines.forEach((line: WidgetLine) => {
        // on prends la valeur de la baseline
        const val: string = this.getRawKeyOrValue(fei, line, fkd, field);
        if (val && val.toString().length > 0 && !isNaN(Number(val))) {
          tmpRes += Number(val);
          nbres++;
        }
      });
    }

    if (nbres > 0) {
      tmpRes = tmpRes / nbres;
    }
    return this.toCleanNumberStr(tmpRes, fkd);
  }

  /**
   * retourne la valeur minimale
   */
  public MIN(fei: FeiVO, lines: WidgetLine[], fkd: FeiKeyDef, field?: string): string {
    let tmpRes: number = null;
    lines.forEach((line: WidgetLine) => {
      // on prends la valeur de la baseline
      const val: string = this.getRawKeyOrValue(fei, line, fkd, field);
      // TODO TODO prendre les 0
      if (val && val.toString().length > 0 && !isNaN(Number(val))) {
        if (!tmpRes || tmpRes > Number(val)) {
          tmpRes = Number(val);
        }
      }
    });
    if (tmpRes) {
      return tmpRes.toString();
    } else {
      return null;
    }
  }

  /**
   * retourne la valeur minimale
   */
  public MAX(fei: FeiVO, lines: WidgetLine[], fkd: FeiKeyDef, field?: string): string {
    let tmpRes: number = null;
    lines.forEach((line: WidgetLine) => {
      // on prends la valeur de la baseline
      const val: string = this.getRawKeyOrValue(fei, line, fkd, field);
      if (val && val.toString().length > 0 && !isNaN(Number(val))) {
        if (!tmpRes || tmpRes < Number(val)) {
          tmpRes = Number(val);
        }
      }
    });
    if (tmpRes) {
      return tmpRes.toString();
    } else {
      return null;
    }
  }

  /**
   * retourne un trilean sur le pool de valeur
   */
   public BOOLEAN(fei: FeiVO, lines: WidgetLine[], fkd: FeiKeyDef, field?: string): string {
    let hasTrue: boolean = false;
    let hasFalse: boolean = false;
    let hasNull: boolean = false;
    lines.forEach((line: WidgetLine) => {
      // on prends la valeur de la baseline
      const val: string = this.getRawKeyOrValue(fei, line, fkd, field);
      if (val === '1') {
        hasTrue = true;
      } else if (val === '0') {
        hasFalse = true;
      } else {
        hasNull = true;
      }
    });

    if (hasNull || (hasTrue && hasFalse)) {
      return null;
    } else if (hasTrue) {
      return '1';
    } else {
      return '0';
    }
  }

  /**
   * retourne la liste des valeurs
   */
   public LIST(fei: FeiVO, lines: WidgetLine[], fkd: FeiKeyDef, field?: string): string {
    let result: string = '';
    let tmpRes: Set<string> = new Set();
    let idx: number = 0; // pas plus de 10
    lines.forEach((line: WidgetLine) => {
      if (idx < 10) {      
        const val: string = this.getRawKeyOrValue(fei, line, fkd, field);
        if (val && val.toString().length > 0 ) {
          tmpRes.add(val);
          idx++;
        }
      }
    });

    if (tmpRes.size > 0) {
      result =  [...tmpRes.values()].join(ViewData.STYLE_ELEMENT_SEPARATOR + ' ');
    }
    // console.log('aggEng', pls, field, tmpRes);
    return result;
  }

  /**
   * compte les true
   */
  public COUNTTRUE(fei: FeiVO, lines: WidgetLine[], fkd: FeiKeyDef, field?: string): number {
    let nb: number = 0;
    lines.forEach((line: WidgetLine) => {
      // on prends la valeur de la baseline
      const val: string = this.getRawKeyOrValue(fei, line, fkd, field);
      if (val === '1') {
        nb++;
      } 
    });
    return nb;
  }

  /**
   * compte les false
   */
  public COUNTFALSE(fei: FeiVO, lines: WidgetLine[], fkd: FeiKeyDef, field?: string): number {
    let nb: number = 0;
    lines.forEach((line: WidgetLine) => {
      // on prends la valeur de la baseline
      const val: string = this.getRawKeyOrValue(fei, line, fkd, field);
      if (val === '0') {
        nb++;
      } 
    });
    return nb;
  }

  /**
   * génération du post-calcul (méthode COMPUTE sur l'aggrégat)
   */
  public PIVOTME(params: any, widget: IWidget, lines: WidgetLine[]): string {
    
    let result: string = ''; // retour, pas de null
    
    const fei: FeiVO = widget.fei as FeiVO;
    const line: WidgetLine = params.data; // ligne source
    const colDef: WizColDef = params.colDef; // définition de la colonne
    
    // TODO TODO ici, pas bon si PMD & agg / totAgg
    let code: string = colDef.field; // champ mesure cible code#mode|fonction d'aggrégat
    
    // la vrai mesure en cas de mesure custom
    let realMeasureCode: string;

    if(!!colDef.wcio.totAggM) { // injection d'une mesure spécifique pour le calcul
      // code = [colDef.fkd.fullCode, colDef.wcio.totAgg].join(WizConstants.KEY_SEPARATOR);
      code = [colDef.wcio.totAggM, colDef.wcio.totAgg].join(WizConstants.KEY_SEPARATOR);
      realMeasureCode = colDef.wcio.totAggM;
    } else {
      // totAgg deprecated
      // code = [colDef.fkd.fullCode, colDef.wcio.totAgg].join(WizConstants.KEY_SEPARATOR);
      code = [colDef.fkd.fullCode, colDef.wcio.agg].join(WizConstants.KEY_SEPARATOR);
      realMeasureCode = colDef.fkd.fullCode;
    }
    
    
    const fkd: FeiKeyDef = params.colDef.fkd; // PKD
   
    let daddy: any = params.column.parent || 'root'; // le groupe d'appartenance, pour mémorisation sur la wl

    // if(code === "M_PA-REAL#I|COMPUTE") {
    //   console.log('code', realMeasureCode);
    // }

    // on mémorise la cellule selon LA COLONNE parente et le code#fist#agg
    if(line.pivotValues?.get(daddy)?.has(code)) {
      let val = line.pivotValues.get(daddy).get(code);
      // console.log('exist', val);
      result = val;
      return result;
    }

    // #### on est pas sortis, traitement du pool ####
    
    // prise des champs en data, pour collecte des aggrégats
    let colAggMap: Map<string, string> = new Map();
    widget.context.d?.forEach((wcio: WidgetConfigurationItemObj) => {
      // colAggMap.set(wcio.cod, wcio.totAgg);
      colAggMap.set(wcio.cod, wcio.agg);
    });

    // on construit la map pour l'ensemble de la matrice
    // la méthode d'aggrégat par défaut, ou la méthode définie dans la colonne 
    let allAggMap: Map<string, string> = new Map();
    fei.orderedMatrix.forEach((mm: MeasMode) => {   
      // if(mm.code === "MES_PMN1#I") {
      //   console.log("MES_PMN1#I", mm);
      // }
      allAggMap.set(mm.code, colAggMap.get(mm.code) || mm.m.defaultAgg || this._model.dataEngine.pkdDefaultAggregateFunction(mm.m.dataKind));
      // if(colAggMap.has(mm.code) && colAggMap.get(mm.code) === this.AGG_COMPUTE) {
      //   allAggMap.set(mm.code, this.AGG_COMPUTE);
      // }
      // allAggMap.set(mm.code, colAggMap.get(mm.code) || DataEngine.pkdDefaultAggregateFunction(mm.m.dataKind));
    });

    // on ajoute la définition pour la colonne custom...
    allAggMap.set(realMeasureCode, AggregateEngine.AGG_COMPUTE);

    // on va calculer les dépendances en récursif
    let mm: MeasMode = fei.matrix.get(realMeasureCode);
    this.recurseCompute(fei, line, lines, mm, allAggMap, daddy, 0);
    
    result = line.pivotValues.get(daddy)?.get(code) || null;
    return result;
  }

  /**
   * calcul récursif des dépendances 
   * @param fei porteur
   * @param line consolidée cible, elle porte les résultats sur pivotValues
   * @param lines du pool à consolider
   * @param mm meaMode cible
   * @param aggMap map des types d'aggrégats par code de mesure
   * @param daddy la colonne de regroupement pour mémoriser le résultat (ou 'Root')
   * @param niveau de calcul pour les groupes
   */
  private recurseCompute(fei: FeiVO, line: WidgetLine, lines: WidgetLine[], 
    mm: MeasMode, aggMap: Map<string, string>, daddy: any, level: number): void {
    // console.log('recurseCompute', mm.code, line.pivotValues);
    // construction en récursif des dépendances
    if(!mm) {
      return;
    }
    if(mm.deps) {// && level === 0) {
      let newLevel: number = level++;
      mm.deps.forEach((mesCode: string) => {
        let depMM: MeasMode = fei.matrix.get(mesCode);
        this.recurseCompute(fei, line, lines, depMM, aggMap, daddy, newLevel);
      });
    }

    // les dépendances sont alimentées je peux me calculer
    let result: string = '';
    let fkd: FeiKeyDef = fei.keyMap.get(mm.code);
    let aggMethod: string = aggMap.get(mm.code);
    if(aggMethod === AggregateEngine.AGG_COMPUTE) {
      // calcul
      let keyPartMap: Map<string, string> = new Map();
      
      [...line.pivotValues?.get(daddy)?.keys() || []].forEach(s => {
        keyPartMap.set(s.split(WizConstants.KEY_SEPARATOR)[0], line.pivotValues.get(daddy).get(s));
      });

      const cr: ComputeResult = FormulaCoreEngine.computeMmiDependencies(mm.mmi, line, fei, true, keyPartMap);
      if(cr?.valor?.length > 0) {
        result = cr.valor;
      }
      

    } else {
      result = this.call(aggMethod, fei, lines, fkd);
    }

    // mémorisation
    
      if(!line.pivotValues) {
        line.pivotValues = new Map();
      }
      if(!line.pivotValues.has(daddy)) {
        line.pivotValues.set(daddy, new Map());
      }
      let key: string = [mm.code, aggMethod].join(WizConstants.KEY_SEPARATOR);
      // let key: string = [targetCodeOnPivotValue].join(WizConstants.KEY_SEPARATOR);
    // if(level > 1) {  
      line.pivotValues.get(daddy).set(key, result);
    // }
  }

  /**
   * calcul d'aggrégat en mode revert
   * @param method 
   * @param widget 
   * @param lines 
   * @param fkd de la mesure (porté par la ligne)
   * @param params de la colonne (pour AGG_COMPUTE) 
   */
  public revertAggregate(method: string, widget: IWidget, lines: WidgetLine[], fkd: FeiKeyDef, params?: any): any {
      
    switch(method) {
      case AggregateEngine.AGG_COMPUTE: 
        // console.log('AGG_COMPUTE', fkd);
        return this.PIVOTMEREVERT(params, widget, lines, fkd)
        // return this.AVG(fei, lines, fkd);
      break;
      default:
        return this.call(method, widget.fei, lines, fkd);

    }  
  } 

  /**
   * génération du post-calcul (méthode COMPUTE sur l'aggrégat) pour le REVERT
   * 
   * @param params 
   * @param widget 
   * @param lines 
   * @param measureFkd 
   * @returns 
   */
  public PIVOTMEREVERT(params: any, widget: IWidget, lines: WidgetLine[], measureFkd: FeiKeyDef): string {
    
    let result: string = ''; // retour, pas de null
    
    const fei: FeiVO = widget.fei as FeiVO;
    const line: WidgetLine = params.data; // ligne source (c'est un Fake, qui correspond à la mesure)
    // const colDef: WizColDef = params.colDef; // définition de la colonne
    
    // TODO TODO ici, pas bon si PMD & agg / totAgg
    // let code: string = colDef.field; // champ mesure cible code#mode|fonction d'aggrégat
    // if(widget.context.pmd && colDef.wcio.totAgg) {
    let code = [measureFkd.fullCode, AggregateEngine.AGG_COMPUTE].join(WizConstants.KEY_SEPARATOR);
    // }
    
    const fkd: FeiKeyDef = measureFkd; // PKD
   
    let daddy: any = params.column.colDef.field || 'root'; // le groupe d'appartenance, pour mémorisation sur la wl

    // on mémorise la cellule selon LA COLONNE parente et le code#fist#agg
    if(line.pivotValues?.get(daddy)?.has(code)) {
      let val = line.pivotValues.get(daddy).get(code);
      // console.log('exist', val);
      result = val;
      return result;
    }

    // #### on est pas sortis, traitement du pool ####
    
    // prise des champs en data, pour collecte des aggrégats
    let colAggMap: Map<string, string> = new Map();
    widget.context.d?.forEach((wcio: WidgetConfigurationItemObj) => {
      
      if(widget.context.pmd) {
        // if(wcio.totAgg === this.AGG_COMPUTE) { // seulement les overrides
          colAggMap.set(wcio.cod, wcio.totAgg);
        // }
      } else {
        // if(wcio.agg === this.AGG_COMPUTE) { // seulement les overrides
          colAggMap.set(wcio.cod, wcio.agg);
        // }
      }

    });

    // on construit la map pour l'ensemble de la matrice
    // la méthode d'aggrégat par défaut, ou la méthode définie dans la colonne 
    let allAggMap: Map<string, string> = new Map();
    fei.orderedMatrix.forEach((mm: MeasMode) => {   
      // if(mm.code === "MES_PMN1#I") {
      //   console.log("MES_PMN1#I", mm);
      // }
      allAggMap.set(mm.code, colAggMap.get(mm.code) || mm.m.defaultAgg || this._model.dataEngine.pkdDefaultAggregateFunction(mm.m.dataKind));
      // if(colAggMap.has(mm.code) && colAggMap.get(mm.code) === this.AGG_COMPUTE) {
      //   allAggMap.set(mm.code, this.AGG_COMPUTE);
      // }
      // allAggMap.set(mm.code, colAggMap.get(mm.code) || DataEngine.pkdDefaultAggregateFunction(mm.m.dataKind));
    });

    // console.log('allAggMap', allAggMap);

    // on va calculer les dépendances en récursif
    let mm: MeasMode = fei.matrix.get(measureFkd.fullCode);
    this.recurseCompute(fei, line, lines, mm, allAggMap, daddy, 0);
    result = line.pivotValues.get(daddy).get(code);
    return result;
  }


}
